import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';

import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import {alpha, Card, CardContent, CardHeader, debounce, Divider, FormControl, InputAdornment, Link, OutlinedInput, Stack, useTheme } from '@mui/material';
import PageBreadcrumbs from '../../../components/PageBreadcrumbs';
import NewsPost from '../../../types/communication/newsPost';
import SearchIcon from '@mui/icons-material/Search';

const News = (): JSX.Element => {
    
    const theme = useTheme();
    const { showBoundary } = useErrorBoundary();  
    const [newsPost, setNewsPosts] = useState<NewsPost[]>();
    const [allPosts, setAllPosts] = useState<NewsPost[]>([]);
    const [toggleViewMore, setToggleViewMore] = useState(true);

    useEffect(() => {
        getNewsPostList();
    }, [])

    const getNewsPostList = async () => {

        try{
          await fetch(`${process.env.REACT_APP_API_BASE_URL}/news/`)
          .then(result => {
            if (!result.ok) {
              showBoundary(new Error('Failed to get news post list.'));
            }
            return result.json();
          })
          .then(result => {
            let count = result.length;
            if (count > 0){
                setNewsPosts(result.slice(0, 15));
                setAllPosts(result);
                const hash = window.location.hash.slice(1); // Remove the '#' character from the hash
                if (hash) {
                  const element = document.getElementById(hash);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth'});
                }
              }
            }
          });
        }catch(error){
            console.error('Uncaught Error:', error);
        }
      }

    const filterByName = debounce((event: any) => {
        let searchInput = event.target.value.toLowerCase();
        if(searchInput.length === 0){
          setNewsPosts(allPosts.slice(0, 15));
        }
        else{
          
            let filteredNews = allPosts.filter((post: any) => post.title.toLowerCase().includes(searchInput) || post.content.toLowerCase().includes(searchInput));
            setNewsPosts(filteredNews);
        }
      }, 800);

    const handleViewMore = () => {
      setToggleViewMore(!toggleViewMore);
      setNewsPosts(allPosts);
    }
      
return (

    <Main>
      <Container>
        <PageBreadcrumbs></PageBreadcrumbs>
        <Typography
            variant="h5"
            align={'left'}
            data-aos={'fade-up'}
            sx={{
                fontWeight: 700,
            }}
            paddingTop={3}
            gutterBottom
            marginBottom={2}
            >
            News and Announcements
        </Typography>
        <Divider/>
        <Grid container marginTop={3}>
          <Grid size={{ xs: 12, md: 10 }} marginBottom={2}>
            <Stack direction={"row"} spacing={1} sx={{ mb: 1}}>
                <FormControl size="small" sx={{ m: 1, p:0, maxWidth: 300 }}>
                  <OutlinedInput
                      placeholder={"Search news..."}
                      startAdornment={
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      }
                      onChange={filterByName}
                  />
                </FormControl>
            </Stack>
          </Grid>
          {
              newsPost?.map((item, i) => (
                  <Grid size={{xs:11, md:9}} key={i} marginBottom={2}>
                    <Card id={`news-${item.id}`} >
                      <CardHeader
                          avatar={
                           <Typography
                              variant={'subtitle2'}
                              fontWeight={550}
                              sx={{
                                bgcolor: alpha(theme.palette.success.light, 0.1),
                                color: theme.palette.success.dark,
                                paddingX: 1.5,
                                paddingY: 0.5,
                                display: 'inline',
                              }}
                            >
                            {item.date}
                           </Typography>
                          }
                          title={ 
                              <Typography variant="subtitle1" fontWeight={550} >
                                  {item.title}
                              </Typography>
                           }
                      />
                      <Divider/>
                      <CardContent>
                          <Typography component={"div"} variant="body2" role="region" aria-label="News content">
                              <div dangerouslySetInnerHTML={{ __html: item.content }}></div>
                          </Typography>
                      </CardContent>
                    </Card>
                </Grid>
              ))
          }
          {
            toggleViewMore &&
            <Grid size={{ xs: 11, md: 9 }} display="flex" justifyContent="flex-end" marginTop={1}>
              <Link className='is-clickable' onClick={handleViewMore} underline='hover' fontSize={"small"}  component={"i"}><i>View More News</i></Link>
            </Grid>
          }
        </Grid>
      </Container>
    </Main>
  );
};

export default News;

